import React, { useEffect, useMemo, useState } from 'react';

import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api';

import { Box } from '@mui/material';

import Spinner from '../../Common/Spinner';

const DEFAULT_CONTAINER_STYLE: React.CSSProperties = {
  width: '100%',
  height: '600px',
};

const DEFAULT_CENTER = { lat: -33.868, lng: 151.209 };
const DEFAULT_ZOOM = 10;

const POINT_PADDING = 0.01;

type MapItem = {
  id: string;
  name: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  geom: { type: string; coordinates: any };
};

type Props = {
  areaMapItems: Array<MapItem>;
  pointMapItems: Array<MapItem>;
  containerStyle?: React.CSSProperties;
};

function expandBoundsWithGeoJson(bounds: google.maps.LatLngBounds, geometry) {
  if (geometry.type === 'Point') {
    const lat = geometry.coordinates[1];
    const lng = geometry.coordinates[0];
    bounds.extend({ lat: lat - POINT_PADDING, lng: lng - POINT_PADDING });
    bounds.extend({ lat: lat + POINT_PADDING, lng: lng + POINT_PADDING });
  } else if (geometry.type === 'Polygon') {
    geometry.coordinates[0].forEach(([lng, lat]) => bounds.extend({ lat, lng }));
  } else if (geometry.type === 'MultiPolygon') {
    geometry.coordinates.forEach((polygon) => {
      polygon[0].forEach(([lng, lat]) => bounds.extend({ lat, lng }));
    });
  }
}

export default function SearchMap({ areaMapItems, pointMapItems, containerStyle = DEFAULT_CONTAINER_STYLE }: Props) {
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: window.configs.GOOGLE_PLACE_API_KEY,
  });

  const markers = useMemo(
    () =>
      pointMapItems.map((item) => ({
        lng: item.geom.coordinates[0],
        lat: item.geom.coordinates[1],
        id: item.id,
        title: item.name,
      })),
    [pointMapItems],
  );

  useEffect(() => {
    if (!map) {
      return;
    }

    map.data.forEach((feature) => map.data.remove(feature));

    if (areaMapItems.length === 0 && pointMapItems.length === 0) {
      map.setCenter(DEFAULT_CENTER);
      map.setZoom(DEFAULT_ZOOM);
      return;
    }

    areaMapItems.forEach((item) => {
      map.data.addGeoJson({
        type: 'FeatureCollection',
        features: [{ type: 'Feature', geometry: item.geom }],
      });
    });

    const bounds = new google.maps.LatLngBounds();
    areaMapItems.forEach((item) => expandBoundsWithGeoJson(bounds, item.geom));
    pointMapItems.forEach((item) => expandBoundsWithGeoJson(bounds, item.geom));

    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, 40);
    }
  }, [areaMapItems, pointMapItems, map]);

  if (loadError) {
    return <Box>Error loading Google Maps</Box>;
  }

  if (!isLoaded) {
    return <Spinner />;
  }

  return (
    <GoogleMap
      center={DEFAULT_CENTER}
      zoom={DEFAULT_ZOOM}
      mapContainerStyle={containerStyle}
      onLoad={(loadedMap) => {
        setMap(loadedMap);
      }}
    >
      {markers.map((marker) => (
        <Marker position={{ lat: marker.lat, lng: marker.lng }} title={marker.title} key={marker.id} />
      ))}
    </GoogleMap>
  );
}
